import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Return type for the useSmartCaptcha hook
 */
export interface UseSmartCaptchaReturn {
  // The smart captcha token received from Yandex SmartCaptcha
  smartToken: string | undefined;
  // Whether the captcha should be called up
  isVisible: boolean;
  // Callback to handle successful captcha completion
  handleSmartCaptcha: (token: string) => void;
  // Show the captcha and set a callback to be executed when captcha is completed
  showCaptcha: (onSuccess?: (token: string) => void) => void;
  // Hide the captcha
  hideCaptcha: () => void;
  // Clear the stored token
  clearToken: () => void;
  isReset: boolean;
  handleReset: (reset: boolean) => void;
}

export function useSmartCaptcha(): UseSmartCaptchaReturn {
  const [smartToken, setSmartToken] = useState<string | undefined>(undefined);
  const [isVisible, setIsVisible] = useState(false);
  const onSuccessCallbackRef = useRef<((token: string) => void) | undefined>(undefined);
  const [isReset, setIsReset] = useState<boolean>(false);
  const isMounted = useRef(false);

  const handleReset = useCallback((value: boolean): void => {
    if (isMounted.current) {
      setIsReset(value);
    }
  }, [isMounted.current]);

  const handleSmartCaptcha = useCallback((token: string): void => {
    setSmartToken(token);
    setIsVisible(false);

    // Execute the success callback if it exists
    if (onSuccessCallbackRef.current) {
      onSuccessCallbackRef.current(token);
      onSuccessCallbackRef.current = undefined; // Clear after execution
    }
  }, [onSuccessCallbackRef.current]);

  const showCaptcha = useCallback((onSuccess?: (token: string) => void) => {
    onSuccessCallbackRef.current = onSuccess;
    setIsVisible(true);
  }, []);

  const hideCaptcha = useCallback(() => {
    setIsVisible(false);
    onSuccessCallbackRef.current = undefined;
  }, [setIsVisible, onSuccessCallbackRef]);

  const clearToken = useCallback(() => {
    if (isMounted.current) {
      setSmartToken(undefined);
    }
  }, [isMounted.current]);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  return {
    smartToken,
    isVisible,
    handleSmartCaptcha,
    showCaptcha,
    hideCaptcha,
    clearToken,
    handleReset,
    isReset,
  };
}
